import React, { useState, useEffect } from 'react';
import "../pages/result/result.css";
import AlertMessage from "../AlertMessage";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';

function PromoteStd() {
  const [educationalYear, setAcademicYear] = useState('');
  const [classValue, setClassValue] = useState('');
  const [targetClass, setTargetClass] = useState('');
  const [targetDivision, setTargetDivision] = useState('');
  const [targetAcademicYear, setTargetAcademicYear] = useState('');
  const [studentData, setStudentData] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [classes, setClasses] = useState([]); 
  const [division, setDivision] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [targetDivisions, setTargetDivisions] = useState([]);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'English');
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [serialNoData, setSerialNoData] = useState({});
  const [isPromoting, setIsPromoting] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

    // New state variables for division management
  const [showAddDivisionDialog, setShowAddDivisionDialog] = useState(false);
  const [newDivisionName, setNewDivisionName] = useState("");
  const [isAddingDivision, setIsAddingDivision] = useState(false);

  const udiseNumber = localStorage.getItem("udiseNumber");
  
  const academicYears = [
    "2020-2021",
    "2021-2022",
    "2022-2023",
    "2023-2024",
    "2024-2025",
    "2025-2026",
    "2026-2027",
    "2027-2028",
    "2028-2029",
  ];

  // IndexedDB constants
  const DB_NAME = 'SchoolManagementDB';
  const STUDENT_STORE = 'studentData';
  const DB_VERSION = 1;

  // Generate next academic year based on current selection
  useEffect(() => {
    if (educationalYear) {
      const [startYear, endYear] = educationalYear.split('-').map(Number);
      setTargetAcademicYear(`${startYear + 1}-${endYear + 1}`);
    }
  }, [educationalYear]);

  // Reset divisions when class changes
  useEffect(() => {
    if (classValue) {
      const divisionsForClass = studentData
        .filter(student => student.currentClass === classValue)
        .map(student => student.division)
        .filter((value, index, self) => value && self.indexOf(value) === index);
      setDivisions(divisionsForClass);
    } else {
      setDivisions([]);
    }
  }, [classValue, studentData]);

  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/defaultSettings.json`);
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setAcademicYear(data.defaultYear || "");
          }
        }
      } catch (error) {
        console.error("Error fetching default settings:", error);
      }
    };

    fetchDefaultSettings();
  }, [udiseNumber]);

  useEffect(() => {
    if (alertMessage) {
      setShowAlert(true);
      const timeoutId = setTimeout(() => {
        setShowAlert(false);
        setAlertMessage("");
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [alertMessage]);

  const openIndexedDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = (event) => {
        console.error("IndexedDB error:", event.target.error);
        reject(event.target.error);
      };
      
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STUDENT_STORE)) {
          db.createObjectStore(STUDENT_STORE, { keyPath: "id" });
        }
      };
    });
  };

  const getStudentById = (store, id) => {
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => {
        console.error("Error getting student:", event.target.error);
        reject(event.target.error);
      };
    });
  };

  const updateStudent = (store, student) => {
    return new Promise((resolve, reject) => {
      const request = store.put(student);
      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => {
        console.error("Error updating student:", event.target.error);
        reject(event.target.error);
      };
    });
  };

  const fetchStudentData = async (db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STUDENT_STORE, "readonly");
      const store = transaction.objectStore(STUDENT_STORE);
      const request = store.getAll();
      
      request.onsuccess = (event) => {
        const students = event.target.result.map(student => ({
          ...student,
          // Ensure compositeKey exists for Firebase updates
          compositeKey: student.compositeKey || `${student.currentClass}-${student.division}-${student.srNo}`
        }));
        
        setStudentData(students);
        
        // Extract classes and divisions
        const classesAndDivisions = {};
        const allClasses = new Set();
        
        students.forEach((student) => {
          if (student.currentClass) {
            if (!classesAndDivisions[student.currentClass]) {
              classesAndDivisions[student.currentClass] = {};
            }
            
            const division = student.division || "";
            if (!classesAndDivisions[student.currentClass][division]) {
              classesAndDivisions[student.currentClass][division] = [];
            }
            
            classesAndDivisions[student.currentClass][division].push(student.id);
            allClasses.add(student.currentClass);
          }
        });

        setSerialNoData(classesAndDivisions);
        setClasses(Array.from(allClasses).sort((a, b) => a.localeCompare(b)));
        resolve(students);
      };
      
      request.onerror = (event) => {
        console.error("Error fetching student data:", event.target.error);
        reject(event.target.error);
      };
    });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const db = await openIndexedDB();
        await fetchStudentData(db);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (classValue && educationalYear) {
      fetchStudentsForClassAndDivision();
    }
  }, [classValue, educationalYear, division, studentData]);

    const fetchStudentsForClassAndDivision = () => {
      const filteredStudents = studentData.filter(
        (student) => 
          student.isActive !== false && 
          student.currentClass === classValue &&
          (division === "" || student.division === division)
      );
    
      setSelectedStudents(filteredStudents);
      setSelectedStudentIds([]);
      setSelectAll(false);
    };
  

  const handleAcademicYearChange = (e) => setAcademicYear(e.target.value);
  
  const handleClassChange = (e) => {
    const selectedClass = e.target.value;
    setClassValue(selectedClass);
    setDivision("");
    
    if (selectedClass) {
      const numericClass = parseInt(selectedClass);
      if (!isNaN(numericClass)) {
        const nextClass = (numericClass + 1).toString();
        if (classes.includes(nextClass)) {
          setTargetClass(nextClass);
          
          const divisionsForTargetClass = studentData
            .filter(student => student.currentClass === nextClass)
            .map(student => student.division)
            .filter((value, index, self) => value && self.indexOf(value) === index);
          
          setTargetDivisions(divisionsForTargetClass);
        }
      }
    }
  };


  const handleTargetClassChange = (e) => {
    const newTargetClass = e.target.value;
    setTargetClass(newTargetClass);
    
    if (newTargetClass) {
      const divisionsForTargetClass = studentData
        .filter(student => student.currentClass === newTargetClass)
        .map(student => student.division)
        .filter((value, index, self) => value && self.indexOf(value) === index);
      
      setTargetDivisions(divisionsForTargetClass);
      setTargetDivision("");
    } else {
      setTargetDivisions([]);
    }
  };

  const handleSelectAllChange = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedStudentIds(checked ? selectedStudents.map(student => student.id) : []);
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };



  const syncWithFirebase = async (studentIds) => {
    try {
      for (const studentId of studentIds) {
        const student = studentData.find(s => s.id === studentId);
        
        if (student) {
          // Extract the srNo from various possible sources
          let srNo;
          
          if (student.serialNo) {
            srNo = student.serialNo;
          } else if (student.srNo) {
            srNo = student.srNo;
          } else if (student.id && !isNaN(parseInt(student.id))) {
            // If student.id is numeric, it might be the srNo
            srNo = student.id;
          }
          
          // Log for debugging
          console.log('Student ID:', studentId);
          console.log('Student Name:', `${student.stdName} ${student.stdSurname}`);
          console.log('Found srNo:', srNo);
          
          if (!srNo) {
            console.error(`Could not determine srNo for student ${student.stdName} ${student.stdSurname}`);
            continue; // Skip this student
          }
          
          // Build the Firebase path using just the srNo
          const firebasePath = `/schoolRegister/${udiseNumber}/studentData/${srNo}`;
          
          console.log('Updating Firebase:', firebasePath);
          
          const response = await fetch(
            `${process.env.REACT_APP_FIREBASE_DATABASE_URL}${firebasePath}.json`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                currentClass: targetClass,
                division: targetDivision || student.division,
                educationalYear: targetAcademicYear
              }),
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
  
          if (!response.ok) {
            throw new Error(`Firebase update failed: ${response.status}`);
          }
        }
      }
      return true;
    } catch (error) {
      console.error("Firebase sync error:", error);
      throw error;
    }
  };

  const promoteStudents = async () => {
    if (!targetClass || !targetAcademicYear) {
      setAlertMessage("Please select target class and academic year");
      return;
    }
  
    if (selectedStudentIds.length === 0) {
      setAlertMessage("Please select at least one student to promote");
      return;
    }

    setIsPromoting(true);
  
    try {
      const db = await openIndexedDB();
      const transaction = db.transaction(STUDENT_STORE, "readwrite");
      const store = transaction.objectStore(STUDENT_STORE);
      
      const updatePromises = selectedStudentIds.map(async (studentId) => {
        const student = await getStudentById(store, studentId);
        
        if (student) {
          const oldId = student.id;
          const newClass = targetClass;
          const newDivision = targetDivision || student.division || '';
          const newYear = targetAcademicYear;
          const srNo = student.serialNo || student.srNo;
          
          // Construct new ID
          const newId = `${newClass}-${newDivision}-${srNo}`;

          // Create updated student object
          const updatedStudent = {
            ...student,
            id: newId,
            currentClass: newClass,
            division: newDivision,
            educationalYear: newYear,
          };

          // Delete the old student entry
          store.delete(oldId);

          // Add the updated one with new key
          store.add(updatedStudent);
        }
      });

      await Promise.all(updatePromises);
      await syncWithFirebase(selectedStudentIds);

      setAlertMessage("Students promoted successfully!");
      fetchStudentsForClassAndDivision();
    } catch (error) {
      console.error("Promotion error:", error);
      setAlertMessage("Error promoting students. Please check console for details.");
    } finally {
      setIsPromoting(false);
    }
  };

    const handleAddDivisionClick = () => {
    setNewDivisionName("");
    setShowAddDivisionDialog(true);
  };

  const handleNewDivisionChange = (e) => {
    setNewDivisionName(e.target.value.toUpperCase()); // Convert to uppercase for consistency
  };

  const addNewDivision = async () => {
    if (!newDivisionName.trim()) {
      setAlertMessage("Please enter a division name");
      return;
    }

    if (!targetClass) {
      setAlertMessage("Please select a target class first");
      setShowAddDivisionDialog(false);
      return;
    }

    setIsAddingDivision(true);

    try {
      // Add the new division to the target divisions list
      setTargetDivisions(prev => [...prev, newDivisionName]);
      setTargetDivision(newDivisionName);
      setAlertMessage(`Division "${newDivisionName}" added successfully`);
    } catch (error) {
      console.error("Error adding division:", error);
      setAlertMessage("Error adding division. Please try again.");
    } finally {
      setIsAddingDivision(false);
      setShowAddDivisionDialog(false);
    }
  };


  return (
    <div>
      <AlertMessage message={alertMessage} show={showAlert} />
      <div className='main-content-of-page'>
        <h3 style={{ color: 'rgb(27, 67, 50)', textAlign: 'center' }}>
          {language === "English" ? "Promote Students" : "विद्यार्थी बढती"}
        </h3>
        <hr/>
        <div className="row mt-4">
          <div className="col-md-5">
            <h5 style={{color:'rgba(4, 69, 125, 0.75)'}}>{language === "English" ? "Current Class Details" : "सध्याचा वर्गाचा तपशील"}</h5>
            <table className="table table-striped table-bordered">
              <tbody>
                <tr>
                  <th>{language === "English" ? "Academic Year" : "शैक्षणिक वर्ष"}</th>
                  <td>
                    <select
                      value={educationalYear}
                      onChange={handleAcademicYearChange}
                      className="form-control"
                    >
                      <option value="">Select Year</option>
                      {academicYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <th>{language === "English" ? "Class" : "वर्ग"}</th>
                  <td>
                    <select
                      value={classValue}
                      onChange={handleClassChange}
                      className="form-control"
                    >
                      <option value="">Select Class</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <th>{language === "English" ? "Division" : "तुकडी"}</th>
                  <td>
                    <select
                      value={division}
                      onChange={(e) => setDivision(e.target.value)}
                      className="form-control"
                    >
                      <option value="">All Divisions</option>
                      {divisions.map(div => (
                        <option key={div} value={div}>{div}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="col-md-1"><ArrowForwardIcon  style={{fontSize:'50px', color:'green', marginTop:'88px'}}/></div>
          <div className="col-md-5">
            <h5  style={{color:'rgba(4, 69, 125, 0.75)'}}>{language === "English" ? "Promote to the next class" : "पुढील वर्गात बढती द्या"}</h5>
            <table className="table table-striped table-bordered">
              <tbody>
                <tr>
                  <th>{language === "English" ? "Target Year" : "लक्ष्य वर्ष"}</th>
                  <td>
                    <select
                      value={targetAcademicYear}
                      onChange={(e) => setTargetAcademicYear(e.target.value)}
                      className="form-control"
                    >
                      <option value="">Select Year</option>
                      {academicYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <th>{language === "English" ? "Target Class" : "लक्ष्य वर्ग"}</th>
                  <td>
                    <select
                      value={targetClass}
                      onChange={handleTargetClassChange}
                      className="form-control"
                    >
                      <option value="">Select Class</option>
                      {classes.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <th>{language === "English" ? "Target Division" : "लक्ष्य तुकडी"}</th>
                  <td>
                    <div className="d-flex">
                      <select
                        value={targetDivision}
                        onChange={(e) => setTargetDivision(e.target.value)}
                        className="form-control"
                        style={{ marginRight: '5px' }}
                      >
                        <option value="">Keep Current</option>
                        {targetDivisions.map(div => (
                          <option key={div} value={div}>{div}</option>
                        ))}
                      </select>
                      <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={handleAddDivisionClick}
                        title={language === "English" ? "Add New Division" : "नवीन तुकडी जोडा"}
                      >
                        <AddIcon fontSize="small" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Add Division Dialog */}
        <Dialog open={showAddDivisionDialog} onClose={() => setShowAddDivisionDialog(false)}>
          <DialogTitle>
            {language === "English" ? "Add New Division" : "नवीन तुकडी जोडा"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={language === "English" ? "Division Name" : "तुकडीचे नाव"}
              type="text"
              fullWidth
              variant="standard"
              value={newDivisionName}
              onChange={handleNewDivisionChange}
              inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            />
            <small className="text-muted">
              {language === "English" 
                ? "Enter a 1-2 letter division name (e.g., A, B, C)" 
                : "1-2 अक्षरी तुकडीचे नाव प्रविष्ट करा (उदा., A, B, C)"}
            </small>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddDivisionDialog(false)}>
              {language === "English" ? "Cancel" : "रद्द करा"}
            </Button>
            <Button 
              onClick={addNewDivision} 
              disabled={!newDivisionName.trim() || isAddingDivision}
            >
              {isAddingDivision 
                ? (language === "English" ? "Adding..." : "जोडत आहे...") 
                : (language === "English" ? "Add Division" : "तुकडी जोडा")}
            </Button>
          </DialogActions>
        </Dialog>
        
        {selectedStudents.length > 0 && (
          <div className="mt-5" style={{margin:'auto', marginBottom:'30px', padding:'30px 10px', boxShadow: '0 4px 15px rgba(89, 7, 7, 0.2)', width:'100%'}}>
            <h6 style={{color:'rgba(89, 7, 7, 0.9)'}}>{language === "English" ? "Select Students To Promote" : "पुढील वर्गात पाठवण्यासाठी विद्यार्थी निवडा."}</h6>
            
            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                />
                <label className="form-check-label">
                  {language === "English" ? "Select All" : "सर्व निवडा"}
                </label>
              </div>
            </div>
            
            <table className="table table-striped table-bordered">
              <thead>
                <tr>
                  <th style={{padding:'4px'}}>Select</th>
                  <th style={{padding:'4px'}}>Roll No</th>
                  <th style={{padding:'4px'}}>Student Name</th>
                  <th style={{padding:'4px'}}>Division</th>
                  <th style={{padding:'4px'}}>Current Class</th>
                </tr>
              </thead>
             <tbody>
  {[...selectedStudents]
    .sort((a, b) => {
      // Convert rollNo to number if they are strings
      const rollA = parseInt(a.rollNo);
      const rollB = parseInt(b.rollNo);
      return rollA - rollB;
    })
    .map(student => (
      <tr key={student.id}>
        <td style={{ padding: '4px' }}>
          <input
            type="checkbox"
            checked={selectedStudentIds.includes(student.id)}
            onChange={() => handleStudentSelection(student.id)}
          />
        </td>
        <td style={{ padding: '4px' }}>{student.rollNo}</td>
        <td style={{ padding: '4px' }}>{`${student.stdName || ''} ${student.stdSurname || ''}`}</td>
        <td style={{ padding: '4px' }}>{student.division || ''}</td>
        <td style={{ padding: '4px' }}>{student.currentClass}</td>
      </tr>
    ))}
</tbody>

            </table>
            
            <div className="text-center mt-3">
              <button 
                className="btn btn-primary" 
                onClick={promoteStudents}
                disabled={isPromoting || selectedStudentIds.length === 0}
              >
                {isPromoting 
                  ? (language === "English" ? "Promoting..." : "बढती देत आहे...") 
                  : (language === "English" ? "Promote Students" : "विद्यार्थ्यांना बढती द्या")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PromoteStd;